import { useState, useMemo, useEffect } from 'react'

export interface UsePaginationOptions {
  itemsPerPage?: number
  totalItems: number
  resetOnChange?: boolean
}

export interface UsePaginationReturn<T> {
  currentPage: number
  totalPages: number
  startIndex: number
  endIndex: number
  paginatedItems: T[]
  goToPage: (page: number) => void
  nextPage: () => void
  previousPage: () => void
  canGoNext: boolean
  canGoPrevious: boolean
}

const DEFAULT_ITEMS_PER_PAGE = 5

export function usePagination<T>(
  items: T[],
  options: UsePaginationOptions
): UsePaginationReturn<T> {
  const { itemsPerPage = DEFAULT_ITEMS_PER_PAGE, totalItems, resetOnChange = true } = options

  const [currentPage, setCurrentPage] = useState(1)

  // Resetar para página 1 quando o total de itens mudar
  useEffect(() => {
    if (resetOnChange) {
      setCurrentPage(1)
    }
  }, [totalItems, resetOnChange])

  // Calcular valores de paginação
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage

  // Itens paginados
  const paginatedItems = useMemo(() => {
    return items.slice(startIndex, endIndex)
  }, [items, startIndex, endIndex])

  // Funções de navegação
  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(totalPages, page))
    setCurrentPage(validPage)
  }

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
  }

  const previousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
  }

  const canGoNext = currentPage < totalPages
  const canGoPrevious = currentPage > 1

  return {
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    paginatedItems,
    goToPage,
    nextPage,
    previousPage,
    canGoNext,
    canGoPrevious,
  }
}
